import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryButton from '../components/PrimaryButton';
import FloatingInput from '../components/FloatingInput';
import Modal from '../components/Modal';
import opendingoLogo from '../assets/img/logo/dingo-logo.png';
import googleIcon from '../assets/img/icons/google-ic.png';
import { useSignupViewModel } from '../viewmodels/SignupViewModel';
import { useAuth } from '../contexts/AuthContext';
import { loginWithGoogleIdToken } from '../api/services/googleAuthService';
import { loginWithAppleIdToken } from '../api/services/appleAuthService';
import { signInWithApple, isFirebaseConfigured } from '../firebase';

declare global {
  interface Window {
    google?: any;
  }
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 384 512" fill="currentColor">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141 4 184.8 4 273.5c0 26.2 4.8 53.3 14.4 81.2 12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
  </svg>
);

const Signup = () => {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const { state, updateField, handleSubmit, isFormValid, closeSuccessModal } = useSignupViewModel();
  const [googleError, setGoogleError] = useState('');
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const [appleError, setAppleError] = useState('');
  const [appleLoading, setAppleLoading] = useState(false);

  const handleAppleSignIn = async () => {
    setAppleError('');
    setAppleLoading(true);
    try {
      const idToken = await signInWithApple();
      const data = await loginWithAppleIdToken(idToken);
      authLogin(data.token, data.user);
      navigate('/dashboard');
    } catch (err: any) {
      if (err?.code === 'auth/popup-closed-by-user') {
        setAppleLoading(false);
        return;
      }
      setAppleError(err.message || 'Connexion Apple impossible.');
    } finally {
      setAppleLoading(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmit();
  };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    // Ce back-office est réservé au staff — "s'inscrire" avec Google ici ne
    // crée jamais de compte, ça ne fait que connecter un compte staff déjà
    // existant (même endpoint et même filtre que la page de connexion).
    const handleCredential = async (response: { credential: string }) => {
      setGoogleError('');
      try {
        const data = await loginWithGoogleIdToken(response.credential);
        authLogin(data.token, data.user);
        navigate('/dashboard');
      } catch (err: any) {
        setGoogleError(err.message || 'Connexion Google impossible.');
      }
    };

    const renderButton = () => {
      if (!window.google || !googleButtonRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredential
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'icon',
        theme: 'outline',
        size: 'large'
      });
    };

    if (window.google) {
      renderButton();
      return;
    }

    const existing = document.getElementById('google-identity-script');
    if (existing) {
      existing.addEventListener('load', renderButton);
      return () => existing.removeEventListener('load', renderButton);
    }

    const script = document.createElement('script');
    script.id = 'google-identity-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = renderButton;
    document.body.appendChild(script);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen h-screen flex flex-col md:flex-row w-full bg-gray-50 overflow-hidden min-h-0">
      {/* Partie gauche (Welcome) */}
      <div className="hidden md:flex md:w-1/2 w-full bg-indigo-500 relative flex-col justify-center items-center p-8 md:p-12 text-white min-h-screen h-full">
        <div className="absolute left-0 bottom-0 w-40 h-40 bg-indigo-300 opacity-30 rounded-full -mb-20 -ml-20 z-0" />
        <div className="absolute right-0 top-0 w-32 h-32 bg-indigo-200 opacity-20 rounded-full -mt-16 -mr-16 z-0" />
        <div className="relative z-10 w-full flex flex-col items-center">
          <div className="flex items-center gap-2 mb-8">
            <span className="text-8xl">🚲</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-2 text-center">Bon retour !</h2>
          <p className="mb-8 text-center text-indigo-100">Prêt à reprendre la route ? Connectez-vous pour continuer l'aventure !</p>
          <PrimaryButton
            variant="outline"
            className="mt-2 px-10 text-white border-white border-2 hover:bg-white hover:text-indigo-600"
            rounded="rounded-none"
            onClick={() => navigate('/signin')}
          >
            Se connecter
          </PrimaryButton>
        </div>
      </div>
      {/* Partie droite (Signup) */}
      <div className="md:w-1/2 w-full flex flex-col justify-start items-center p-4 md:p-12 bg-white h-screen overflow-y-auto pt-8">
        {/* Logo au-dessus du titre sur mobile uniquement */}
        <div className="flex justify-center mb-6">
          <img src={opendingoLogo} alt="OpenDingo" className="w-20 h-20 object-contain" />
        </div>

        <h2 className="text-2xl md:text-3xl font-bold text-indigo-600 mb-4 text-center">Rejoignez-nous !</h2>
        <div className="flex gap-4 justify-center mb-4">
          {GOOGLE_CLIENT_ID ? (
            <div className="relative w-10 h-10">
              <button type="button" className="w-10 h-10 flex items-center justify-center rounded-md border border-gray-200 hover:bg-indigo-50 transition">
                <img src={googleIcon} alt="Google" className="w-5 h-5" />
              </button>
              {/* Vrai bouton Google, invisible, superposé pour recevoir le clic */}
              <div ref={googleButtonRef} className="absolute inset-0 overflow-hidden opacity-0" />
            </div>
          ) : (
            <button type="button" className="w-10 h-10 flex items-center justify-center rounded-md border border-gray-200 hover:bg-indigo-50 transition">
              <img src={googleIcon} alt="Google" className="w-5 h-5" />
            </button>
          )}
          {isFirebaseConfigured && (
            <button
              type="button"
              onClick={handleAppleSignIn}
              disabled={appleLoading}
              className="w-10 h-10 flex items-center justify-center rounded-md border border-gray-200 hover:bg-indigo-50 transition disabled:opacity-50"
            >
              <AppleIcon />
            </button>
          )}
        </div>
        <div className="flex items-center mb-4 w-full">
          <div className="flex-1 h-px bg-gray-300"></div>
          <span className="px-4 text-gray-400 text-sm">or</span>
          <div className="flex-1 h-px bg-gray-300"></div>
        </div>

        {/* Messages d'erreur/succès */}
        {(state.message || googleError || appleError) && (
          <div className={`w-full p-3 rounded-md mb-4 text-center ${
            state.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {googleError || appleError || state.message}
          </div>
        )}

        <form className="w-full flex flex-col gap-4" onSubmit={handleFormSubmit}>
          <div className="flex flex-col md:flex-row gap-4">
            <FloatingInput
              label="Nom"
              name="name"
              type="text"
              placeholder="Votre nom"
              value={state.user.name}
              onChange={e => updateField('name', e.target.value)}
              className="w-full md:w-1/2"
            />
            <FloatingInput
              label="Prénom"
              name="surname"
              type="text"
              placeholder="Votre prénom"
              value={state.user.surname}
              onChange={e => updateField('surname', e.target.value)}
              className="w-full md:w-1/2"
            />
          </div>
          <FloatingInput
            label="Téléphone"
            name="phone"
            type="tel"
            placeholder="+216 22 345 678"
            value={state.user.phone}
            onChange={e => updateField('phone', e.target.value)}
          />
          <FloatingInput
            label="Date de naissance"
            name="birthDate"
            type="date"
            value={state.user.birthDate}
            onChange={e => updateField('birthDate', e.target.value)}
          />
          <FloatingInput
            label="Adresse email"
            name="email"
            type="email"
            placeholder="you@email.com"
            value={state.user.email}
            onChange={e => updateField('email', e.target.value)}
          />
          <FloatingInput
            label="Mot de passe"
            name="password"
            type="password"
            placeholder="********"
            value={state.user.password}
            onChange={e => updateField('password', e.target.value)}
          />
          <FloatingInput
            label="Confirmation du mot de passe"
            name="confirmPassword"
            type="password"
            placeholder="********"
            value={state.user.confirmPassword}
            onChange={e => updateField('confirmPassword', e.target.value)}
          />
          <PrimaryButton
            fullWidth
            className="mt-2"
            rounded="rounded-md"
            bgColor="bg-indigo-600"
            type="submit"
            disabled={!isFormValid() || state.isLoading}
          >
            {state.isLoading ? 'Création en cours...' : 'Créer mon compte'}
          </PrimaryButton>
        </form>
        <div className="mt-6 text-center text-sm text-gray-500 md:hidden">
          Vous avez déjà un compte ?{' '}
          <a href="/signin" className="text-indigo-600 hover:underline font-medium">Connectez-vous</a>
        </div>
      </div>

      {/* Modal de succès */}
      {state.showSuccessModal && (
        <Modal
          open={state.showSuccessModal}
          onClose={closeSuccessModal}
          title="Yoohoo ! 🎉"
          footer={
            <div className="w-full">
              <PrimaryButton
                onClick={closeSuccessModal}
                bgColor="bg-indigo-600"
                rounded="rounded-md"
                fullWidth
              >
                OK
              </PrimaryButton>
            </div>
          }
        >
          <div className="text-center py-4">
            <div className="text-6xl mb-4">🎉</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Compte créé avec succès !
            </h3>
            <p className="text-gray-600">
              Votre compte a été créé avec succès. Vous allez être redirigé vers la page de connexion.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Signup; 